/**
 * Step 6.4 -- the stance controllers.
 *
 * A stance is an animation bundle, a weapon loadout, and nothing else. It never
 * constrains appearance: a plate-armoured archer is legitimate, and nothing in this
 * file reads a slot.
 *
 * Both classes are the same shape -- a state machine feeding the upper layer while
 * locomotion owns the legs, with whole-body clips for the reactions. They differ
 * only in vocabulary, which is why they share one class rather than subclassing:
 *
 *   swordsman   three-hit combos with `_ReturnToIdle` links, the block triad,
 *               directional hits, four deaths each with a 1-frame resting pose
 *   archer      `Undrawn -> ToDrawn -> Drawn -> Reload` as explicit states, the shot,
 *               and a five-pose additive set for free aim
 *
 * A stance is always a **composition**: the client keeps a movement clip on the lower
 * layer at all times, so the combat clips are asked for on the torso mask and sit over
 * whatever the legs are doing. The reactions are the exception and take the whole
 * body. Each clip is therefore asked for by the role it is played under, never
 * "whichever role it happened to be bound as".
 *
 * The archer's bow is a skinned prop with its own clips; the swordsman's sword is an
 * unrigged mesh on a socket. That difference lives in `sockets.ts`, not here.
 */
import type { StanceClass } from '@dungeon-builder/shared';

import type { AnimationLayerRig } from './animationLayerRig.ts';
import type { AttachedWeapon } from './sockets.ts';

/** Clip ids a stance drives, resolved against whatever the pack actually shipped. */
export interface StanceClips {
  /** Resting upper-body pose. */
  idle?: string;
  /** Attack chain, in order. Each link may be interrupted by the next. */
  combo: string[];
  /** Whole-body reaction. */
  hit?: string;
  /** Draw-cycle states, archer only. */
  draw?: { undrawn?: string; toDrawn?: string; drawn?: string; reload?: string; shoot?: string };
  /** Additive aim offsets, archer only: neutral, up, down, left, right. */
  aim?: { neutral?: string; up?: string; down?: string; left?: string; right?: string };
}

export type StanceState = 'idle' | 'attacking' | 'drawing' | 'drawn' | 'reacting';

export interface StanceControllerOptions {
  stance: StanceClass;
  rig: AnimationLayerRig;
  clips: StanceClips;
  weapon?: AttachedWeapon | null;
}

/**
 * Picks the clips a stance needs out of whatever a pack enabled.
 *
 * A pack is curated, so any clip may be missing; the controller degrades to what is
 * there rather than throwing. Matching is by id substring because clip ids are derived
 * from filenames and a builder may have relabelled the display name but never the id.
 */
export function resolveStanceClips(stance: StanceClass, available: string[]): StanceClips {
  const find = (...needles: string[]): string | undefined =>
    available.find((id) => needles.every((needle) => id.includes(needle)));

  if (stance === 'archer') {
    return {
      idle: find('combat/', 'idle_base_with_bow') ?? find('combat/', 'idle_base'),
      combo: [find('combat/', 'shoot_to_aiming'), find('combat/', 'aiming_reload')].filter(
        (id): id is string => Boolean(id),
      ),
      hit: find('combat/', 'stagger'),
      draw: {
        undrawn: find('combat/', 'aiming_undrawn'),
        toDrawn: find('combat/', 'aiming_to_drawn'),
        drawn: find('combat/', 'aiming_drawn'),
        reload: find('combat/', 'aiming_reload'),
        shoot: find('combat/', 'shoot'),
      },
      aim: {
        neutral: find('additive_n'),
        up: find('additive_u90'),
        down: find('additive_d90'),
        left: find('additive_l90'),
        right: find('additive_r90'),
      },
    };
  }

  return {
    idle: find('combat/idle'),
    // The three light-combo links, in order. Each ships a `_ReturnToIdle` companion so
    // the chain can be abandoned cleanly at any link.
    combo: ['light_combo01_a', 'light_combo01_b', 'light_combo01_c']
      .map((link) => find('combat/attack/', link))
      .filter((id): id is string => Boolean(id)),
    hit: find('combat/', 'stagger'),
  };
}

export class StanceController {
  private state: StanceState = 'idle';
  private link = 0;

  constructor(private readonly options: StanceControllerOptions) {}

  get current(): StanceState {
    return this.state;
  }

  /** Settles into the stance's resting pose. */
  rest(): void {
    const { rig, clips } = this.options;
    this.state = 'idle';
    this.link = 0;
    if (clips.idle && rig.has(clips.idle, 'upper')) {
      rig.play('upper', clips.idle, { blendMs: 200 });
      this.syncProp(clips.idle);
    }
  }

  /**
   * Advances the attack chain. Called again mid-swing, it takes the next link -- which
   * is what a three-hit combo is.
   */
  attack(): void {
    const { rig, clips } = this.options;
    if (clips.combo.length === 0) return;

    const id = clips.combo[this.link % clips.combo.length];
    if (!id || !rig.has(id, 'upper')) return;

    this.state = 'attacking';
    this.link += 1;
    rig.play('upper', id, { blendMs: 80, loop: false });
    this.syncProp(id);
  }

  /** Archer only: hold the draw. */
  draw(): void {
    const { rig, clips } = this.options;
    const id = clips.draw?.toDrawn ?? clips.draw?.drawn;
    if (!id || !rig.has(id, 'upper')) return;
    this.state = 'drawing';
    rig.play('upper', id, { blendMs: 120, loop: false });
    this.syncProp(id);
  }

  /** Archer only: free aim, as an additive offset over the resolved pose. */
  aim(x: number, y: number): void {
    const { rig, clips } = this.options;
    if (!clips.aim) return;
    const set = (id: string | undefined, weight: number): void => {
      if (id && rig.has(id, 'additive')) rig.setAdditive(id, Math.max(0, Math.min(1, weight)));
    };
    set(clips.aim.right, x);
    set(clips.aim.left, -x);
    set(clips.aim.up, y);
    set(clips.aim.down, -y);
  }

  /**
   * A hit takes the whole body. A character struck mid-run staggers with its legs too,
   * not from the waist up, which is what the override mode exists for.
   */
  react(onDone?: () => void): void {
    const { rig, clips } = this.options;
    if (!clips.hit || !rig.has(clips.hit, 'full')) return;
    this.state = 'reacting';
    rig.play('full', clips.hit, {
      loop: false,
      blendMs: 60,
      onComplete: () => {
        this.rest();
        onDone?.();
      },
    });
  }

  /**
   * Plays the bow's own clip alongside the body's.
   *
   * The body animation never learns which bow is equipped -- it is the prop clip that
   * knows, which is why swapping bow type is a matter of swapping which prop clip
   * plays and nothing else.
   */
  private syncProp(bodyClip: string): void {
    const weapon = this.options.weapon;
    if (!weapon) return;
    for (const group of weapon.propClips.values()) group.stop();
    const prop = weapon.propClips.get(bodyClip);
    prop?.play(false);
  }

  dispose(): void {
    this.options.weapon?.dispose();
  }
}
